import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Plus, FileText, Trash2, Download, Edit, Copy, Search,
  ArrowLeft, Mail, Package, Truck, ClipboardList, Save,
  AlertTriangle, X
} from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';

const DOC_LABELS = { DDT: 'DDT', ORDINE: 'Ordine (O.R.)', RDO: 'Richiesta Offerta (RDO)' };
const STATUS_LABELS = { bozza: 'Bozza', confermato: 'Confermato', inviato: 'Inviato', annullato: 'Annullato' };
const STATUS_COLORS = { bozza: 'bg-yellow-100 text-yellow-800', confermato: 'bg-green-100 text-green-800', inviato: 'bg-blue-100 text-blue-800', annullato: 'bg-red-100 text-red-800' };

const emptyForm = {
  doc_type: 'DDT', date: new Date().toISOString().split('T')[0], commessa: '',
  supplier_id: '', supplier: { name: '', address: '', phone: '', email: '' },
  destination: { name: '', address: '', city: '', province: '', zip_code: '', phone: '', contact_person: '' }, rows: [], notes: '', status: 'bozza',
  ddt_data: { causale: '', trasporto_mezzo: '', vettore_name: '', vettore_address: '', data_ritiro: '', ora_ritiro: '', peso: '', colli: '', porto: '', aspetto_beni: 'A VISTA' },
  order_data: { trasporto: '', pagamento: '', centro_costo: '' },
  totals: { subtotal: 0, vat_amount: 0, total: 0 }
};

const emptyRow = { code: '', description: '', unit: 'PZ', quantity: 1, price: 0, discount_pct: 0, vat_pct: 22, total: 0 };

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [activeView, setActiveView] = useState('list');
  const [editingDoc, setEditingDoc] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [suppliers, setSuppliers] = useState([]);
  const [sites, setSites] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inventorySearch, setInventorySearch] = useState('');
  const [showInventoryDropdown, setShowInventoryDropdown] = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [carrierSearch, setCarrierSearch] = useState('');
  const [showCarrierDropdown, setShowCarrierDropdown] = useState(false);
  const [fieldMemory, setFieldMemory] = useState({
    ddt_causale: [], ddt_trasporto_mezzo: [], ddt_porto: [], ddt_aspetto_beni: [],
    order_trasporto: [], order_pagamento: [], order_centro_costo: [], row_unit: [],
    manual_supplier_name: [], manual_destination_name: [],
  });

  const loadDocuments = useCallback(async () => {
    try {
      const res = await api.get('/documents');
      setDocuments(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadMasterData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        api.get('/suppliers'), api.get('/sites'), api.get('/inventory'),
        api.get('/carriers'), api.get('/addresses'), api.get('/equipment')
      ]);
      if (results[0].status === 'fulfilled') setSuppliers(results[0].value.data);
      if (results[1].status === 'fulfilled') setSites(results[1].value.data);
      if (results[2].status === 'fulfilled') setInventoryItems(results[2].value.data);
      if (results[3].status === 'fulfilled') setCarriers(results[3].value.data);
      if (results[4].status === 'fulfilled') setSavedAddresses(results[4].value.data);
      if (results[5].status === 'fulfilled') setEquipmentItems(results[5].value.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadFieldMemory = useCallback(async () => {
    const fields = ['ddt_causale', 'ddt_trasporto_mezzo', 'ddt_porto', 'ddt_aspetto_beni', 'order_trasporto', 'order_pagamento', 'order_centro_costo', 'row_unit', 'manual_supplier_name', 'manual_destination_name'];
    try {
      const results = await Promise.allSettled(fields.map((f) => api.get('/field-memory', { params: { field: f } })));
      const next = {};
      fields.forEach((f, i) => { next[f] = results[i].status === 'fulfilled' ? results[i].value.data : []; });
      setFieldMemory(next);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadDocuments(); loadMasterData(); loadFieldMemory(); }, [loadDocuments, loadMasterData, loadFieldMemory]);

  const recalcTotals = (rows) => {
    let subtotal = 0, vatAmount = 0;
    const updated = rows.map(r => {
      const qty = parseFloat(r.quantity) || 0;
      const price = parseFloat(r.price) || 0;
      const disc = parseFloat(r.discount_pct) || 0;
      const vat = parseFloat(r.vat_pct) || 0;
      const lineTotal = qty * price * (1 - disc / 100);
      subtotal += lineTotal;
      vatAmount += lineTotal * vat / 100;
      return { ...r, total: Math.round(lineTotal * 100) / 100 };
    });
    return { rows: updated, totals: { subtotal: Math.round(subtotal * 100) / 100, vat_amount: Math.round(vatAmount * 100) / 100, total: Math.round((subtotal + vatAmount) * 100) / 100 } };
  };

  const handleNewDoc = () => {
    setEditingDoc(null);
    setForm({ ...emptyForm, date: new Date().toISOString().split('T')[0], rows: [{ ...emptyRow }] });
    setSupplierSearch('');
    setSiteSearch('');
    setActiveView('edit');
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setForm({
      ...emptyForm, ...doc,
      supplier: { ...emptyForm.supplier, ...(doc.supplier || {}) },
      destination: { ...emptyForm.destination, ...(doc.destination || {}) },
      ddt_data: { ...emptyForm.ddt_data, ...(doc.ddt_data || {}) },
      order_data: { ...emptyForm.order_data, ...(doc.order_data || {}) },
      totals: { ...emptyForm.totals, ...(doc.totals || {}) }
    });
    setSupplierSearch(doc.supplier?.name || '');
    setSiteSearch(doc.destination?.name || '');
    setActiveView('edit');
  };

  const handleDuplicateDoc = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/duplicate`);
      loadDocuments();
    } catch (e) { alert('Errore duplicazione'); }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Eliminare ${DOC_LABELS[doc.doc_type]} n. ${doc.number}?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      loadDocuments();
    } catch (e) { alert('Errore eliminazione'); }
  };

  const handleSave = async () => {
    if (!form.supplier?.name) { alert('Seleziona un fornitore/cessionario'); return; }
    setSaving(true);
    try {
      if (editingDoc) {
        await api.put(`/documents/${editingDoc.id}`, form);
      } else {
        const res = await api.post('/documents', form);
        setEditingDoc(res.data);
        setForm(prev => ({ ...prev, id: res.data.id, number: res.data.number }));
      }
      loadDocuments();
      loadFieldMemory();
      alert('Documento salvato!');
    } catch (e) { alert('Errore salvataggio: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleDownloadPDF = async (docId) => {
    try {
      const id = docId || editingDoc?.id;
      if (!id) { alert('Salva prima il documento'); return; }
      const res = await api.get(`/documents/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1] || 'documento.pdf';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore generazione PDF'); }
  };

  const handleShowEmail = async (docId) => {
    try {
      const id = docId || editingDoc?.id;
      if (!id) { alert('Salva prima il documento'); return; }
      const res = await api.get(`/documents/${id}/email-suggestion`);
      setEmailModal(res.data);
    } catch (e) { alert('Errore email suggestion'); }
  };

  const selectSupplier = (sup) => {
    setForm(prev => ({
      ...prev, supplier_id: sup.id,
      supplier: { name: sup.company_name || sup.name, address: `${sup.address || ''} ${sup.city || ''} ${sup.province || ''}`.trim(), phone: sup.phone || '', email: sup.email || '' }
    }));
    setSupplierSearch(sup.company_name || sup.name);
    setShowSupplierDropdown(false);
  };

  const selectSite = (site) => {
    setForm(prev => ({
      ...prev,
      destination: {
        name: site.name, address: (site.address || site.location || '').trim(),
        city: site.city || '', province: site.province || '', zip_code: site.zip_code || '',
        phone: site.phone || '', contact_person: site.contact_person || '',
      }
    }));
    setSiteSearch(site.name);
    setShowSiteDropdown(false);
  };

  const selectCarrier = (carrier) => {
    setForm(prev => ({
      ...prev,
      ddt_data: {
        ...prev.ddt_data,
        vettore_name: carrier.company_name || carrier.name,
        vettore_address: [carrier.address, carrier.city, carrier.province].filter(Boolean).join(', ')
      }
    }));
    setCarrierSearch(carrier.company_name || carrier.name);
    setShowCarrierDropdown(false);
  };

  const selectSavedAddress = (addr) => {
    setForm(prev => ({
      ...prev,
      destination: {
        name: addr.name, address: addr.address || '',
        city: addr.city || '', province: addr.province || '', zip_code: addr.zip_code || '',
        phone: addr.phone || '', contact_person: addr.contact_person || '',
      }
    }));
    setSiteSearch(addr.name);
    setShowSiteDropdown(false);
  };

  const selectInventoryItem = (item, rowIdx) => {
    setForm(prev => {
      const newRows = [...prev.rows];
      newRows[rowIdx] = {
        ...newRows[rowIdx],
        code: item.internal_code || item.code || item.id || '',
        description: item.name || item.description || '',
        unit: item.unit_of_measure || item.unit || 'PZ',
        price: item.last_price || item.average_price || item.standard_price || item.price || 0,
        vat_pct: item.default_vat_pct ?? item.vat_pct ?? 22
      };
      const { rows, totals } = recalcTotals(newRows);
      return { ...prev, rows, totals };
    });
    setShowInventoryDropdown(false);
    setInventorySearch('');
  };

  const updateRow = (idx, field, value) => {
    setForm(prev => {
      const newRows = [...prev.rows];
      newRows[idx] = { ...newRows[idx], [field]: value };
      if (['quantity', 'price', 'discount_pct', 'vat_pct'].includes(field)) {
        const { rows, totals } = recalcTotals(newRows);
        return { ...prev, rows, totals };
      }
      return { ...prev, rows: newRows };
    });
  };

  const addRow = () => setForm(prev => ({ ...prev, rows: [...prev.rows, { ...emptyRow }] }));
  const removeRow = (idx) => {
    setForm(prev => {
      const newRows = prev.rows.filter((_, i) => i !== idx);
      const { rows, totals } = recalcTotals(newRows);
      return { ...prev, rows, totals };
    });
  };

  const filteredDocs = documents.filter(d => {
    if (typeFilter !== '__all__' && d.doc_type !== typeFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (d.number || '').toLowerCase().includes(s) || (d.supplier?.name || '').toLowerCase().includes(s) || (d.destination?.name || '').toLowerCase().includes(s);
    }
    return true;
  });

  const filteredSuppliers = suppliers.filter(s => !supplierSearch || (s.company_name || s.name || '').toLowerCase().includes(supplierSearch.toLowerCase()) || (s.vat_number || '').toLowerCase().includes(supplierSearch.toLowerCase()));
  const filteredSites = [
    ...sites.filter(s => !siteSearch || s.name?.toLowerCase().includes(siteSearch.toLowerCase())).map(s => ({...s, _type: 'site'})),
    ...savedAddresses.filter(a => !siteSearch || a.name?.toLowerCase().includes(siteSearch.toLowerCase()) || (a.address || '').toLowerCase().includes(siteSearch.toLowerCase())).map(a => ({...a, _type: 'address'}))
  ];
  const filteredInventory = [
    ...inventoryItems.filter(i => {
      if (!inventorySearch) return true;
      const s = inventorySearch.toLowerCase();
      if ((i.internal_code || '').toLowerCase().includes(s)) return true;
      if ((i.category || '').toLowerCase().includes(s)) return true;
      return (i.name || '').toLowerCase().includes(s) || (i.code || '').toLowerCase().includes(s) || (i.description || '').toLowerCase().includes(s);
    }).map(i => ({...i, _type: 'inventory'})),
    ...equipmentItems.filter(e => {
      if (!inventorySearch) return true;
      const s = inventorySearch.toLowerCase();
      return (e.name || '').toLowerCase().includes(s) || (e.internal_code || '').toLowerCase().includes(s) || (e.category || '').toLowerCase().includes(s);
    }).map(e => ({id: e.id, code: e.internal_code || e.serial_number || e.id.slice(0,8), name: e.name, unit: 'CAD.', last_price: 0, _type: 'equipment', category: e.category}))
  ];
  const filteredCarriers = carriers.filter(c => !carrierSearch || (c.company_name || c.name || '').toLowerCase().includes(carrierSearch.toLowerCase()));

  // LIST VIEW
  if (activeView === 'list') {
    return (
      <div className="space-y-4" data-testid="documents-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="documents-title">Documenti</h1>
            <p className="text-sm text-slate-500">DDT, Ordini di Acquisto e Richieste di Offerta</p>
          </div>
          <Button onClick={handleNewDoc} data-testid="new-document-btn">
            <Plus size={16} className="mr-2" /> Nuovo Documento
          </Button>
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Cerca per numero, fornitore..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="documents-search" />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[200px]" data-testid="documents-type-filter">
                  <SelectValue placeholder="Tipo documento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i tipi</SelectItem>
                  <SelectItem value="DDT">DDT</SelectItem>
                  <SelectItem value="ORDINE">Ordini (O.R.)</SelectItem>
                  <SelectItem value="RDO">RDO</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-12 text-slate-400">Caricamento...</div>
            ) : filteredDocs.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Nessun documento trovato</p>
                <Button variant="outline" className="mt-3" onClick={handleNewDoc}>Crea il primo documento</Button>
              </div>
            ) : (
              <div className="overflow-visible">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Numero</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Fornitore / Cessionario</TableHead>
                      <TableHead>Destinazione</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Totale</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocs.map(doc => (
                      <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                        <TableCell>
                          <Badge variant="outline" className="text-xs font-medium">
                            {doc.doc_type === 'DDT' && <Truck size={12} className="mr-1" />}
                            {doc.doc_type === 'ORDINE' && <Package size={12} className="mr-1" />}
                            {doc.doc_type === 'RDO' && <ClipboardList size={12} className="mr-1" />}
                            {DOC_LABELS[doc.doc_type]}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono font-medium">{doc.number}</TableCell>
                        <TableCell>{doc.date ? fmtDate(doc.date) : '-'}</TableCell>
                        <TableCell>{doc.supplier?.name || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{doc.destination?.name || '-'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[doc.status] || 'bg-slate-100 text-slate-700'}`}>
                            {STATUS_LABELS[doc.status] || doc.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {doc.doc_type === 'ORDINE' && doc.totals?.total ? `${doc.totals.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €` : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleEditDoc(doc)} title="Modifica" data-testid={`edit-doc-${doc.id}`}><Edit size={14} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDownloadPDF(doc.id)} title="Scarica PDF" data-testid={`pdf-doc-${doc.id}`}><Download size={14} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => handleShowEmail(doc.id)} title="Suggerisci Email" data-testid={`email-doc-${doc.id}`}><Mail size={14} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDuplicateDoc(doc)} title="Duplica" data-testid={`dup-doc-${doc.id}`}><Copy size={14} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteDoc(doc)} title="Elimina" className="text-red-500 hover:text-red-700" data-testid={`del-doc-${doc.id}`}><Trash2 size={14} /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Suggestion Modal */}
        <Dialog open={!!emailModal} onOpenChange={() => setEmailModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Mail size={18} /> Suggerimento Email</DialogTitle>
            </DialogHeader>
            {emailModal && (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Destinatario</Label>
                  <Input value={emailModal.recipient_email || ''} readOnly className="bg-slate-50" data-testid="email-recipient" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Oggetto</Label>
                  <Input value={emailModal.subject || ''} readOnly className="bg-slate-50" data-testid="email-subject" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Corpo Email</Label>
                  <Textarea value={emailModal.body || ''} readOnly rows={6} className="bg-slate-50" data-testid="email-body" />
                </div>
                <p className="text-xs text-slate-400">Copia e incolla questi dati nel tuo client email. L'invio diretto non e supportato.</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailModal(null)}>Chiudi</Button>
              <Button onClick={() => { navigator.clipboard.writeText(`Oggetto: ${emailModal?.subject}\n\n${emailModal?.body}`); alert('Testo copiato!'); }} data-testid="copy-email-btn">Copia Testo</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // EDIT VIEW
  return (
    <div className="space-y-4" data-testid="document-edit-view">
      <div className="flex items-center gap-3 flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={() => { setActiveView('list'); setEditingDoc(null); }} data-testid="back-to-list-btn">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">
            {editingDoc ? `${DOC_LABELS[form.doc_type]} n. ${editingDoc.number}` : 'Nuovo Documento'}
          </h1>
        </div>
        <div className="flex gap-2">
          {editingDoc && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleShowEmail()} data-testid="edit-email-btn">
                <Mail size={14} className="mr-1" /> Email
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDownloadPDF()} data-testid="edit-pdf-btn">
                <Download size={14} className="mr-1" /> PDF
              </Button>
            </>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-document-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Document Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Type & Basic Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Dati Documento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Tipo Documento</Label>
                  <Select value={form.doc_type} onValueChange={v => setForm(prev => ({ ...prev, doc_type: v }))} disabled={!!editingDoc}>
                    <SelectTrigger data-testid="doc-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DDT">DDT</SelectItem>
                      <SelectItem value="ORDINE">Ordine (O.R.)</SelectItem>
                      <SelectItem value="RDO">RDO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Data</Label>
                  <Input type="date" value={form.date} onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))} data-testid="doc-date" />
                </div>
                <div>
                  <Label className="text-xs">Commessa</Label>
                  <Input value={form.commessa} onChange={e => setForm(prev => ({ ...prev, commessa: e.target.value }))} placeholder="Rif. commessa" data-testid="doc-commessa" />
                </div>
                <div>
                  <Label className="text-xs">Stato</Label>
                  <Select value={form.status} onValueChange={v => setForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger data-testid="doc-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bozza">Bozza</SelectItem>
                      <SelectItem value="confermato">Confermato</SelectItem>
                      <SelectItem value="inviato">Inviato</SelectItem>
                      <SelectItem value="annullato">Annullato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Supplier */}
              <div className="relative">
                <Label className="text-xs">{form.doc_type === 'DDT' ? 'Cessionario' : 'Fornitore'}</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={supplierSearch}
                    onChange={e => { setSupplierSearch(e.target.value); setShowSupplierDropdown(true); }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                    placeholder="Cerca fornitore..."
                    className="pl-8"
                    data-testid="supplier-search"
                  />
                  {form.supplier?.name && (
                    <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => { setForm(prev => ({ ...prev, supplier_id: '', supplier: { name: '', address: '', phone: '', email: '' } })); setSupplierSearch(''); }}>
                      <X size={12} />
                    </Button>
                  )}
                </div>
                {showSupplierDropdown && filteredSuppliers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-72 overflow-y-auto" data-testid="supplier-dropdown">
                    <div className="px-3 py-1 bg-slate-50 text-[10px] font-semibold text-slate-500 uppercase border-b sticky top-0">{filteredSuppliers.length} fornitori</div>
                    {filteredSuppliers.slice(0, 15).map(s => (
                      <button key={s.id} className="w-full text-left px-3 py-2.5 hover:bg-blue-50 text-sm border-b last:border-0 transition-colors" onClick={() => selectSupplier(s)}>
                        <div className="font-semibold text-slate-800">{s.company_name || s.name}</div>
                        <div className="text-xs text-slate-500">{s.address}</div>
                        {s.vat_number && <div className="text-[10px] text-slate-400">P.IVA: {s.vat_number}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 bg-slate-50 rounded-lg p-3">
                <div className="col-span-2">
                  <Label className="text-xs">Nome / Ragione Sociale</Label>
                  <Input list="dl-supplier-name" value={form.supplier.name} onChange={e => setForm(prev => ({ ...prev, supplier: { ...prev.supplier, name: e.target.value } }))} placeholder="Anche se non e' in anagrafica fornitori" data-testid="supplier-name-manual" />
                  <datalist id="dl-supplier-name">{fieldMemory.manual_supplier_name.map((v) => <option key={v} value={v} />)}</datalist>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Indirizzo</Label>
                  <Input value={form.supplier.address} onChange={e => setForm(prev => ({ ...prev, supplier: { ...prev.supplier, address: e.target.value } }))} data-testid="supplier-address-manual" />
                </div>
                <div>
                  <Label className="text-xs">Telefono</Label>
                  <Input value={form.supplier.phone} onChange={e => setForm(prev => ({ ...prev, supplier: { ...prev.supplier, phone: e.target.value } }))} data-testid="supplier-phone-manual" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input value={form.supplier.email} onChange={e => setForm(prev => ({ ...prev, supplier: { ...prev.supplier, email: e.target.value } }))} data-testid="supplier-email-manual" />
                </div>
              </div>

              {/* Destination */}
              <div className="relative">
                <Label className="text-xs">{form.doc_type === 'DDT' ? 'Luogo di Destinazione' : 'Destinazione Merce / Cantiere'}</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={siteSearch}
                    onChange={e => { setSiteSearch(e.target.value); setShowSiteDropdown(true); }}
                    onFocus={() => setShowSiteDropdown(true)}
                    onBlur={() => setTimeout(() => setShowSiteDropdown(false), 200)}
                    placeholder="Cerca cantiere/destinazione..."
                    className="pl-8"
                    data-testid="site-search"
                  />
                  {form.destination?.name && (
                    <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => { setForm(prev => ({ ...prev, destination: { name: '', address: '' } })); setSiteSearch(''); }}>
                      <X size={12} />
                    </Button>
                  )}
                </div>
                {showSiteDropdown && filteredSites.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="site-dropdown">
                    {filteredSites.slice(0, 12).map(s => (
                      <button key={`${s._type}-${s.id}`} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-0" onClick={() => s._type === 'address' ? selectSavedAddress(s) : selectSite(s)}>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${s._type === 'site' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>{s._type === 'site' ? 'Cantiere' : 'Indirizzo'}</span>
                          <span className="font-medium">{s.company_name || s.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 ml-12">{[s.address, s.city, s.province].filter(Boolean).join(', ')}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 bg-slate-50 rounded-lg p-3">
                <div className="col-span-2">
                  <Label className="text-xs">Nome Destinazione</Label>
                  <Input list="dl-destination-name" value={form.destination.name} onChange={e => setForm(prev => ({ ...prev, destination: { ...prev.destination, name: e.target.value } }))} placeholder="Anche se non e' un cantiere gia' censito" data-testid="destination-name-manual" />
                  <datalist id="dl-destination-name">{fieldMemory.manual_destination_name.map((v) => <option key={v} value={v} />)}</datalist>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Indirizzo</Label>
                  <Input value={form.destination.address} onChange={e => setForm(prev => ({ ...prev, destination: { ...prev.destination, address: e.target.value } }))} data-testid="destination-address-manual" />
                </div>
                <div>
                  <Label className="text-xs">CAP</Label>
                  <Input value={form.destination.zip_code} onChange={e => setForm(prev => ({ ...prev, destination: { ...prev.destination, zip_code: e.target.value } }))} data-testid="destination-zip-manual" />
                </div>
                <div>
                  <Label className="text-xs">Citta</Label>
                  <Input value={form.destination.city} onChange={e => setForm(prev => ({ ...prev, destination: { ...prev.destination, city: e.target.value } }))} data-testid="destination-city-manual" />
                </div>
                <div>
                  <Label className="text-xs">Provincia</Label>
                  <Input value={form.destination.province} onChange={e => setForm(prev => ({ ...prev, destination: { ...prev.destination, province: e.target.value } }))} maxLength={2} data-testid="destination-province-manual" />
                </div>
                <div>
                  <Label className="text-xs">Telefono</Label>
                  <Input value={form.destination.phone} onChange={e => setForm(prev => ({ ...prev, destination: { ...prev.destination, phone: e.target.value } }))} data-testid="destination-phone-manual" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Referente</Label>
                  <Input value={form.destination.contact_person} onChange={e => setForm(prev => ({ ...prev, destination: { ...prev.destination, contact_person: e.target.value } }))} data-testid="destination-contact-manual" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DDT-specific fields */}
          {form.doc_type === 'DDT' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Truck size={16} /> Dati Trasporto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Causale del Trasporto</Label>
                  <Textarea value={form.ddt_data.causale} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, causale: e.target.value } }))} rows={3} placeholder="Es: Trasferimento materiale per..." data-testid="ddt-causale" />
                  {fieldMemory.ddt_causale.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {fieldMemory.ddt_causale.slice(0, 6).map((v) => (
                        <button key={v} type="button" className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50" onClick={() => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, causale: v } }))} data-testid={`causale-suggestion-${v}`}>
                          {v.length > 40 ? v.slice(0, 40) + '...' : v}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Trasporto a mezzo</Label>
                    <Input list="dl-trasporto-mezzo" value={form.ddt_data.trasporto_mezzo} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, trasporto_mezzo: e.target.value } }))} placeholder="Vettore / Mittente" data-testid="ddt-trasporto-mezzo" />
                    <datalist id="dl-trasporto-mezzo">{fieldMemory.ddt_trasporto_mezzo.map((v) => <option key={v} value={v} />)}</datalist>
                  </div>
                  <div className="relative">
                    <Label className="text-xs">Vettore (Nome)</Label>
                    <Input
                      value={carrierSearch || form.ddt_data.vettore_name}
                      onChange={e => { setCarrierSearch(e.target.value); setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, vettore_name: e.target.value } })); setShowCarrierDropdown(true); }}
                      onFocus={() => setShowCarrierDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCarrierDropdown(false), 200)}
                      placeholder="Cerca o scrivi vettore..."
                      data-testid="ddt-vettore"
                    />
                    {showCarrierDropdown && filteredCarriers.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-36 overflow-y-auto">
                        {filteredCarriers.slice(0, 8).map(c => (
                          <button key={c.id} className="w-full text-left px-2 py-1.5 hover:bg-slate-50 text-xs border-b last:border-0" onMouseDown={() => selectCarrier(c)}>
                            <div className="font-medium">{c.company_name || c.name}</div>
                            <div className="text-slate-500">{[c.address, c.city, c.province].filter(Boolean).join(', ')}{c.plate ? ` - Tg: ${c.plate}` : ''}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Indirizzo Vettore</Label>
                    <Input value={form.ddt_data.vettore_address} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, vettore_address: e.target.value } }))} placeholder="Via..." data-testid="ddt-vettore-addr" />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Data Ritiro</Label>
                    <Input type="date" value={form.ddt_data.data_ritiro} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, data_ritiro: e.target.value } }))} data-testid="ddt-data-ritiro" />
                  </div>
                  <div>
                    <Label className="text-xs">Ora Ritiro</Label>
                    <Input type="time" value={form.ddt_data.ora_ritiro} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, ora_ritiro: e.target.value } }))} data-testid="ddt-ora-ritiro" />
                  </div>
                  <div>
                    <Label className="text-xs">Peso (kg)</Label>
                    <Input value={form.ddt_data.peso} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, peso: e.target.value } }))} placeholder="kg" data-testid="ddt-peso" />
                  </div>
                  <div>
                    <Label className="text-xs">N. Colli</Label>
                    <Input type="number" value={form.ddt_data.colli} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, colli: e.target.value } }))} data-testid="ddt-colli" />
                  </div>
                  <div>
                    <Label className="text-xs">Porto</Label>
                    <Input list="dl-porto" value={form.ddt_data.porto} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, porto: e.target.value } }))} placeholder="Franco / Assegnato" data-testid="ddt-porto" />
                    <datalist id="dl-porto">{fieldMemory.ddt_porto.map((v) => <option key={v} value={v} />)}</datalist>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Aspetto Esteriore Beni</Label>
                  <Input list="dl-aspetto" value={form.ddt_data.aspetto_beni} onChange={e => setForm(prev => ({ ...prev, ddt_data: { ...prev.ddt_data, aspetto_beni: e.target.value } }))} data-testid="ddt-aspetto" />
                  <datalist id="dl-aspetto">{fieldMemory.ddt_aspetto_beni.map((v) => <option key={v} value={v} />)}</datalist>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ORDINE/RDO specific */}
          {(form.doc_type === 'ORDINE' || form.doc_type === 'RDO') && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Package size={16} /> Condizioni</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Trasporto</Label>
                    <Input list="dl-order-trasporto" value={form.order_data.trasporto} onChange={e => setForm(prev => ({ ...prev, order_data: { ...prev.order_data, trasporto: e.target.value } }))} placeholder="Es: Vs trasporto" data-testid="order-trasporto" />
                    <datalist id="dl-order-trasporto">{fieldMemory.order_trasporto.map((v) => <option key={v} value={v} />)}</datalist>
                  </div>
                  <div>
                    <Label className="text-xs">Pagamento</Label>
                    <Input list="dl-order-pagamento" value={form.order_data.pagamento} onChange={e => setForm(prev => ({ ...prev, order_data: { ...prev.order_data, pagamento: e.target.value } }))} placeholder="Es: Bonifico bancario" data-testid="order-pagamento" />
                    <datalist id="dl-order-pagamento">{fieldMemory.order_pagamento.map((v) => <option key={v} value={v} />)}</datalist>
                  </div>
                  <div>
                    <Label className="text-xs">Destinazione / Centro di Costo</Label>
                    <Input list="dl-order-centro-costo" value={form.order_data.centro_costo} onChange={e => setForm(prev => ({ ...prev, order_data: { ...prev.order_data, centro_costo: e.target.value } }))} placeholder="Es: Magazzino" data-testid="order-centro-costo" />
                    <datalist id="dl-order-centro-costo">{fieldMemory.order_centro_costo.map((v) => <option key={v} value={v} />)}</datalist>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <datalist id="dl-row-unit">{fieldMemory.row_unit.map((v) => <option key={v} value={v} />)}</datalist>

          {/* Items Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Righe Documento</CardTitle>
                <Button size="sm" variant="outline" onClick={addRow} data-testid="add-row-btn">
                  <Plus size={14} className="mr-1" /> Aggiungi Riga
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-visible">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Codice</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="w-[70px]">U.M.</TableHead>
                      <TableHead className="w-[80px]">Q.ta</TableHead>
                      {form.doc_type === 'ORDINE' && (
                        <>
                          <TableHead className="w-[90px]">Prezzo</TableHead>
                          <TableHead className="w-[70px]">Sc. %</TableHead>
                          <TableHead className="w-[70px]">IVA %</TableHead>
                          <TableHead className="w-[100px] text-right">Totale</TableHead>
                        </>
                      )}
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.rows.map((row, idx) => (
                      <TableRow key={idx} data-testid={`item-row-${idx}`}>
                        <TableCell className="p-1">
                          <div className="relative">
                            <Input
                              value={row.code}
                              onChange={e => { updateRow(idx, 'code', e.target.value); setInventorySearch(e.target.value); setActiveRowIdx(idx); setShowInventoryDropdown(true); }}
                              onFocus={() => { setActiveRowIdx(idx); setInventorySearch(row.code); setShowInventoryDropdown(true); }}
                              onBlur={() => setTimeout(() => setShowInventoryDropdown(false), 200)}
                              placeholder="Codice"
                              className="h-8 text-xs"
                              data-testid={`row-code-${idx}`}
                            />
                            {showInventoryDropdown && activeRowIdx === idx && filteredInventory.length > 0 && (
                              <div className="absolute z-50 w-[340px] mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-72 overflow-y-auto" data-testid={`inventory-dropdown-${idx}`}>
                                <div className="px-2 py-1 bg-slate-50 text-[10px] font-semibold text-slate-500 uppercase border-b sticky top-0">Risultati ({filteredInventory.length})</div>
                                {filteredInventory.slice(0, 15).map(item => (
                                  <button key={item.id} className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-0 transition-colors" onMouseDown={() => selectInventoryItem(item, idx)}>
                                    <div className="font-semibold text-blue-800">{item.internal_code || item.code || item.id?.slice(0,8)}</div>
                                    <div className="text-slate-600 text-xs truncate">{item.name}</div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{item.unit_of_measure || item.unit || 'PZ'}</span>
                                      {item.last_price > 0 && <span className="text-xs text-green-700 font-medium">{item.last_price}€</span>}
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">{item._type === 'equipment' ? 'Attr.' : 'Mag.'}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="p-1">
                          <div className="relative">
                            <Input
                              value={row.description}
                              onChange={e => { updateRow(idx, 'description', e.target.value); setInventorySearch(e.target.value); setActiveRowIdx(idx); setShowInventoryDropdown(true); }}
                              onFocus={() => { setActiveRowIdx(idx); setInventorySearch(row.description || ''); setShowInventoryDropdown(true); }}
                              onBlur={() => setTimeout(() => setShowInventoryDropdown(false), 200)}
                              placeholder="Cerca articolo o scrivi descrizione..."
                              className="h-8 text-xs"
                              data-testid={`row-desc-${idx}`}
                            />
                            {showInventoryDropdown && activeRowIdx === idx && inventorySearch && filteredInventory.length > 0 && (
                              <div className="absolute z-50 w-[420px] mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-80 overflow-y-auto" data-testid={`desc-dropdown-${idx}`}>
                                <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-semibold text-slate-500 uppercase border-b sticky top-0 flex justify-between">
                                  <span>Magazzino / Attrezzatura / DPI</span>
                                  <span>{filteredInventory.length} risultati</span>
                                </div>
                                {filteredInventory.slice(0, 20).map(item => {
                                  const searchLower = inventorySearch.toLowerCase();
                                  const nameHtml = (item.name || '').replace(new RegExp(`(${inventorySearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'), '<mark class="bg-yellow-200 rounded px-0.5">$1</mark>');
                                  const codeStr = item.internal_code || item.code || '';
                                  const codeHtml = codeStr.replace(new RegExp(`(${inventorySearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'), '<mark class="bg-yellow-200 rounded px-0.5">$1</mark>');
                                  return (
                                    <button key={item.id} className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b last:border-0 transition-colors group" onMouseDown={() => selectInventoryItem(item, idx)}>
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-0.5">
                                            <span className="font-bold text-blue-800 text-sm" dangerouslySetInnerHTML={{__html: codeHtml}} />
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 flex-shrink-0">{item._type === 'equipment' ? 'Attrezzatura' : 'Magazzino'}</span>
                                          </div>
                                          <p className="text-sm text-slate-700 leading-tight" dangerouslySetInnerHTML={{__html: nameHtml}} />
                                          {item.category && <p className="text-xs text-slate-400 mt-0.5">{item.category}</p>}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                          {item.last_price > 0 && <p className="text-sm font-semibold text-green-700">{item.last_price}€</p>}
                                          <p className="text-[10px] text-slate-400">{item.unit_of_measure || item.unit || 'PZ'}</p>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="p-1">
                          <Input list="dl-row-unit" value={row.unit} onChange={e => updateRow(idx, 'unit', e.target.value)} className="h-8 text-xs" data-testid={`row-unit-${idx}`} />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input type="number" value={row.quantity} onChange={e => updateRow(idx, 'quantity', e.target.value)} className="h-8 text-xs" min="0" data-testid={`row-qty-${idx}`} />
                        </TableCell>
                        {form.doc_type === 'ORDINE' && (
                          <>
                            <TableCell className="p-1">
                              <Input type="number" value={row.price} onChange={e => updateRow(idx, 'price', e.target.value)} className="h-8 text-xs" min="0" step="0.01" data-testid={`row-price-${idx}`} />
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" value={row.discount_pct} onChange={e => updateRow(idx, 'discount_pct', e.target.value)} className="h-8 text-xs" min="0" max="100" data-testid={`row-disc-${idx}`} />
                            </TableCell>
                            <TableCell className="p-1">
                              <Input type="number" value={row.vat_pct} onChange={e => updateRow(idx, 'vat_pct', e.target.value)} className="h-8 text-xs" min="0" data-testid={`row-vat-${idx}`} />
                            </TableCell>
                            <TableCell className="p-1 text-right font-mono text-xs">
                              {row.total ? `${row.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €` : '-'}
                            </TableCell>
                          </>
                        )}
                        <TableCell className="p-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeRow(idx)} data-testid={`remove-row-${idx}`}>
                            <Trash2 size={12} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Note</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} placeholder="Note aggiuntive..." data-testid="doc-notes" />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-4">
          {/* Totals */}
          {form.doc_type === 'ORDINE' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Totali</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotale (IVA esclusa)</span>
                  <span className="font-mono">{form.totals.subtotal.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">IVA</span>
                  <span className="font-mono">{form.totals.vat_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>Totale</span>
                  <span className="font-mono text-blue-700">{form.totals.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Azioni Rapide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full justify-start" size="sm" onClick={handleSave} disabled={saving} data-testid="sidebar-save-btn">
                <Save size={14} className="mr-2" /> Salva Documento
              </Button>
              {editingDoc && (
                <>
                  <Button variant="outline" className="w-full justify-start" size="sm" onClick={() => handleDownloadPDF()} data-testid="sidebar-pdf-btn">
                    <Download size={14} className="mr-2" /> Scarica PDF
                  </Button>
                  <Button variant="outline" className="w-full justify-start" size="sm" onClick={() => handleShowEmail()} data-testid="sidebar-email-btn">
                    <Mail size={14} className="mr-2" /> Suggerimento Email
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Document Info */}
          {editingDoc && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Info Documento</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-slate-500 space-y-1">
                <div>Numero: <strong className="text-slate-700">{editingDoc.number}</strong></div>
                <div>Tipo: <strong className="text-slate-700">{DOC_LABELS[editingDoc.doc_type]}</strong></div>
                <div>Creato: {editingDoc.created_at ? new Date(editingDoc.created_at).toLocaleString('it-IT') : '-'}</div>
                <div>Aggiornato: {editingDoc.updated_at ? new Date(editingDoc.updated_at).toLocaleString('it-IT') : '-'}</div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Email Modal in edit view */}
      <Dialog open={!!emailModal} onOpenChange={() => setEmailModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} /> Suggerimento Email</DialogTitle>
          </DialogHeader>
          {emailModal && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Destinatario</Label>
                <Input value={emailModal.recipient_email || ''} readOnly className="bg-slate-50" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Oggetto</Label>
                <Input value={emailModal.subject || ''} readOnly className="bg-slate-50" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Corpo Email</Label>
                <Textarea value={emailModal.body || ''} readOnly rows={6} className="bg-slate-50" />
              </div>
              <p className="text-xs text-slate-400">Copia e incolla questi dati nel tuo client email.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModal(null)}>Chiudi</Button>
            <Button onClick={() => { navigator.clipboard.writeText(`Oggetto: ${emailModal?.subject}\n\n${emailModal?.body}`); alert('Testo copiato!'); }}>Copia Testo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
